import { ComponentValidations } from "../../../lib/types/form/component-validations";
import { ComponentId } from "../../../lib/types/ids";
import { ChangeEvent, KeyboardEventHandler } from "react";

export interface TextFieldProps {
  id?: ComponentId;
  name: string;
  icon?: string;
  label: string;
  validations?: ComponentValidations | undefined;
  placeholder: string;
  type: string;
  autoComplete?: string;
  showLabel: boolean;
  required: boolean;
  onChange?: (value: string, id: string) => void;
  error?: boolean;
  errorText?: string;
  autoFocus?: boolean;
}

function formatIbanInputElement(textBox: HTMLInputElement, lastKeyIsBackspace: boolean): string {
  const text = textBox.value || "";
  const selectionStart = textBox.selectionStart ?? 0;
  const position = Math.max(0, selectionStart);
  const positionWithoutSpaces = selectionStart - Array.from(text).slice(0, selectionStart).filter((x) => /\s/.test(x)).length;
  const textWithoutSpaces = Array.from(text).filter((x) => !/\s/.test(x)).slice(0, 34).join("");
  const spacePositionAddition = Math.floor(positionWithoutSpaces / 4);
  let newPosition = positionWithoutSpaces + spacePositionAddition;
  const newText = textWithoutSpaces.match(/.{1,4}/g)?.join(" ") ?? "";

  //console.log(`position: ${position}`);
  //console.log(`newText: ${newText}`);
  //console.log(`newText[position]: ${newText[position]}`);
  //console.log(`newText[newPosition]: ${newText[newPosition]}`);
  //console.log(`newPosition: ${newPosition}`);

  // Korrektur für CaretIndex bei Entf- und BackSpace-Tasten.
  if (newPosition > 0
    && newText[position] === ' ') {
    if (position === newPosition
      && (position % 5) === 4
      && !lastKeyIsBackspace) {
      newPosition++;
      //console.log("newPosition++");
    }
    else if (
      position !== newPosition
      && lastKeyIsBackspace) {
      newPosition--;
      //console.log("newPosition--");
    }
  }

  if (text !== newText) {
    textBox.value = newText;
  }

  requestAnimationFrame(() => {
    textBox.selectionStart = newPosition;
    textBox.selectionEnd = newPosition;
  });

  return newText;
}

export default function TextField({
  id,
  name,
  icon,
  label,
  type,
  validations,
  autoComplete,
  placeholder,
  showLabel,
  required,
  onChange,
  error,
  errorText,
  autoFocus,
}: TextFieldProps) {
  let lastKeyIsBackspace: boolean = false;
  const inputOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (validations?.isIBAN){
       value = formatIbanInputElement(e.target, lastKeyIsBackspace);
      }

    if (onChange && id) {
      if (validations?.isIBAN) {
        value = String(value)
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "");
      }

      return onChange(value, id);
    }

    return () => null
  };

  const inputOnKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    //console.log("inputOnKeyDown", e);
    lastKeyIsBackspace = e.code === "Backspace";
  };

  return (
    <div className="text-left w-full component"
      data-id={!id ? null : ComponentId.encode(id)}>
      <div className={`text-sm mb-2 ${!label || !showLabel ? "hidden" : null}`}>
        <label
          id={[name, "label"].join("-")}
          htmlFor={[name, "input"].join("-")}
        >
          {label}
        </label>
      </div>
      <div className={`text-left rounded-text-field h-16 relative`}>
        {icon ? (
          <div className="absolute" style={{ left: "24px", top: "20px" }}>
            <div className="w-5 h-5 flex items-center">
              <img src={icon} />
            </div>
          </div>
        ) : null}
        <input
          className={`h-full w-full rounded-text-field ${icon ? "pl-16" : "pl-8"
            } pr-8 border ${error ? "border-red-500" : "border-gray-300"
            }  focus:ring-0 ${error ? "focus:border-red-500" : "focus:border-primary"
            }`}
          autoComplete={autoComplete}
          id={[id || name, "input"].join("-")}
          name={name}
          placeholder={placeholder}
          required={required}
          type={type}
          autoFocus={autoFocus}
          onChange={inputOnChange}
          onKeyDown={inputOnKeyDown}
        />
      </div>
      {error ? <p className="mt-1 text-xs text-red-500">{errorText}</p> : null}
    </div>
  );
}
